import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorMessage from '../../Components/Message/ErrorMessage';
import messages from '../../Components/Message/messages';
import {
  appleLogin,
  clearSession,
  googleLogin,
  isReloginRequired,
  persistLogin,
  persistSession,
  selectStore,
} from '../../Redux/Auth/authApi';
import {
  isAppleLoginEnabled,
  isAppleSignInCancel,
  signInWithApple,
} from '../../utils/appleAuth';
import AppleSignInButton from '../../Components/Auth/AppleSignInButton';
import { LoginResponse } from '../../types/company';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Link,
} from '@mui/material';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID as string;

interface GoogleLoginResponse {
  credential: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isTokenValid = (): boolean => {
    const accessToken = localStorage.getItem('accessToken');
    const expiration = localStorage.getItem('tokenExpiration');
    if (!accessToken || !expiration) return false;

    const now = new Date().getTime();
    return now < parseInt(expiration, 10);
  };

  // ログイン成功後の共通処理（Google / Apple 共通）。
  const completeLogin = async (login: LoginResponse): Promise<void> => {
    // 一時トークン・所属一覧を保存
    persistLogin(login);

    if (login.memberships.length === 0) {
      // 所属店舗が無い（未登録 or 削除済み）。理論上は 403 で catch に来るが念のため。
      setError(messages.errors.notRegistered);
      return;
    }

    if (login.memberships.length === 1 && !login.memberships[0].locked) {
      // 所属が 1 件のみ かつ ロックされていない → 自動選択して従来通り遷移
      const auth = await selectStore(login.memberships[0].storeId);
      persistSession(auth);
      navigate(`/home?storeClerkId=${auth.clerkId}`);
      return;
    }

    // 複数所属、または唯一の店舗がロック中 → 店舗選択ページへ（ロック解除もここで行う）
    navigate('/select-store');
  };

  // ログイン失敗の共通エラー表示（Google / Apple 共通）。
  const showLoginError = (error: Error, provider: 'google' | 'apple'): void => {
    console.error('Error:', error);
    // 所属店舗が無い（未登録 or 削除済み）・削除リクエスト中（ロック）は専用メッセージを表示する。
    if (error.message === 'NOT_REGISTERED') {
      // Apple はプライベートリレー（メールを非公開）だと招待 email と一致しない旨も案内する（feast-be ADR 0074）。
      setError(
        provider === 'apple'
          ? `${messages.errors.notRegistered}（Apple で「メールを非公開」を選んだ場合、招待されたメールアドレスと一致しないためログインできません）`
          : messages.errors.notRegistered,
      );
    } else if (error.message === 'STORE_LOCKED') {
      setError(messages.errors.storeLocked);
    } else {
      setError(messages.errors.fetchFailed);
    }
  };

  const handleGoogleLogin = (response: GoogleLoginResponse): void => {
    setIsLoading(true);
    googleLogin(response.credential)
      .then(completeLogin)
      .catch((error: Error) => showLoginError(error, 'google'))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAppleLogin = (): void => {
    setError(null);
    setIsLoading(true);
    signInWithApple()
      .then((result) => appleLogin(result.idToken, result.name))
      .then(completeLogin)
      .catch((error: unknown) => {
        if (isAppleSignInCancel(error)) return; // ポップアップを閉じただけなら何も表示しない
        showLoginError(error instanceof Error ? error : new Error(String(error)), 'apple');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    // 認証 localStorage 変更による強制再ログイン（feast-fe ADR 0019）。
    // 残存する旧セッションを破棄してログイン画面に留める（自動ホーム遷移を抑止）。
    if (isReloginRequired()) {
      clearSession();
    }

    const storeClerkId = localStorage.getItem('clerk_id');

    if (isTokenValid() && storeClerkId) {
      navigate(`/home?storeClerkId=${storeClerkId}`);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleLogin,
      });

      window.google.accounts.id.renderButton(
        document.getElementById('google-login-button'),
        { theme: 'filled_blue', size: 'large', shape: 'pill' }
      );
      setIsLoading(false);
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [navigate]);

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" gutterBottom>
            FEASTへログインする
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            id="google-login-button"
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              mt: 2,
            }}
          />

          {isAppleLoginEnabled() && (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 1.5 }}>
              <AppleSignInButton
                label="Appleでサインイン"
                onClick={handleAppleLogin}
                disabled={isLoading}
              />
            </Box>
          )}

          <Divider sx={{ width: '100%', my: 2 }} />
          <Typography variant="body2">
            FEAST をはじめて使う方は{' '}
            <Link component="button" type="button" onClick={() => navigate('/register')}>
              新規登録
            </Link>
          </Typography>

        </Paper>
      </Box>
    </Container>
  );
};

export default Login;